<template>
  <page-outline 
    :title="listTitle"
    :show-filter="true"
    :show-refresh="true" 
    :show-extract="listItems.length > 0"
    :filter-expanded="displayFilter"
    :refresh-disabled="activateSpinner"
    :extract-disabled="false"
    @toggle-filter="toggleFilter"
    @refresh="onRefresh"
    @extract="extractXLSX">
    <template slot="filterContent">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('Webheader.metricsData')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearFilterFields"
        @apply-filters="onRefresh"
      />
    </template>
    <template slot="pageContent">
      <b-container fluid class="listPage">
        <b-row v-if="tabName !== undefined" class="justify-content-start">
          <!-- {{engineOptions}} -->
          <b-col sm="6" lg="3" cols="12" class="filter-item-action mb-5">
            <label class="filter-item-title text-ellipsis" for="enginekey">
              {{ $t('common.alerts.engine') }}
            </label>
            <select class="filter-item-content" v-model="engineIndex" @change="engineUpdate" id="enginekey"
              style="max-width:100%;padding:0.5rem;"
              autocomplete="off">
              <option v-for="(engine, index) in engineOptions" :key="index" :value="engine.id" :disabled="engine.disabled">
                {{ engine.name }}
              </option>
            </select>
          </b-col>
          <b-col sm="6" lg="3" cols="12" class="filter-item-action mb-5">
            <label class="filter-item-title text-ellipsis" for="metrickey">
              {{ $t('common.alerts.metrics_key') }}
            </label>
            <select class="filter-item-content" v-model="tabName" id="metrickey"
              style="max-width:100%;padding:0.5rem;"
              autocomplete="off">
              <option v-for="tab in tabs" :key="tab.index" :value="tab.index">
                {{ tab.translationKey }}
              </option>
            </select>
          </b-col>
        </b-row>
        <spinner v-if="tabName === undefined"></spinner>
        <b-tabs v-model="tabName" @input="onTabChange" nav-class="d-none" v-else>
          <b-tab v-for="(metricsKey, index) in metricsKeys" :key="metricsKey" :title="getMetricsKeyTranslation(metricsKey)">
            <b-row class="justify-content-center mt-5">
              <spinner v-if="activateSpinner"></spinner>
              <b-col v-else-if="metricsChartsData[metricsKey]" cols="12">
                <h2 class="text-center">
                  {{ getMetricsKeyTranslation(metricsKey) }}
                </h2>
                <div class="chart-toggle-buttons">
                  <app-button
                    :text="$t('common.monitoring.line')"
                    :variant="chartType === 'line' ? 'primary' : 'light'"
                    custom-class="px-4"
                    @click="chartType = 'line'">
                  </app-button>
                  <app-button
                    :text="$t('common.monitoring.bar')"
                    :variant="chartType === 'bar' ? 'primary' : 'light'"
                    custom-class="px-4"
                    @click="chartType = 'bar'">
                  </app-button>
                </div>
                <component
                  :is="chartType === 'line' ? 'line-chart' : 'bar-chart'"
                  :ref="`${chartType}Chart_${metricsKey}`"
                  :chart-data="metricsChartsData[metricsKey]"
                  :options="chartType === 'line' ? chartOptions2 : chartOptions3"
                  :canvas-id="`${chartType}-chart-${metricsKey}`"
                  class="mt-2">
                </component>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab v-for="(metricsUnit, index) in metricsUnits" :key="metricsUnit" :title="$t('common.monitoring.resource_usage_over_time_unit', { metricsUnit: metricsUnit })">
            <b-row class="justify-content-center mt-5">
              <spinner v-if="activateSpinner"></spinner>
              <b-col v-else-if="metricsChartsDataUnit[metricsUnit]" cols="12">
                <h2 class="text-center">
                  {{ $t('common.monitoring.resource_usage_over_time_unit', { metricsUnit: metricsUnit }) }}
                </h2>
                <div class="chart-toggle-buttons">
                  <app-button
                    :text="$t('common.monitoring.line')"
                    :variant="chartType === 'line' ? 'primary' : 'light'"
                    custom-class="px-4"
                    @click="chartType = 'line'">
                  </app-button>
                  <app-button
                    :text="$t('common.monitoring.bar')"
                    :variant="chartType === 'bar' ? 'primary' : 'light'"
                    custom-class="px-4"
                    @click="chartType = 'bar'">
                  </app-button>
                </div>
                <component
                  :is="chartType === 'line' ? 'line-chart' : 'bar-chart'"
                  :ref="`${chartType}Chart_${metricsUnit}`"
                  :chart-data="metricsChartsDataUnit[metricsUnit]"
                  :options="chartType === 'line' ? chartOptions2 : chartOptions3"
                  :canvas-id="`${chartType}-chart-unit-${metricsUnit}`">
                </component>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import moment from 'moment-timezone'
import dateTimePicker from '@/components/common/dateTimePicker.vue'
import formWriteGroupLabelInput from '@/components/common/formWriteGroupLabelInput.vue'
import PageOutline from '@/components/common/PageOutline.vue'
import Spinner from '@/components/common/spinner.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppPanel from '@/components/common/AppPanel.vue'
import { errorMessageAlert } from '@/helpers/alert'
import { loadPage } from '@/helpers/routing'
import { Chart as ChartJS } from 'chart.js'
import { Bar } from 'vue-chartjs'
import { Line } from 'vue-chartjs'
import * as XLSX from 'xlsx'
import conf from "@/config/config.dev";

export default {
  name: 'MetricsDataPage',
  components: {
    'datetime-picker-custom': dateTimePicker,
    'form-write-group-label-input': formWriteGroupLabelInput,
    'page-outline': PageOutline,
    'spinner': Spinner,
    'app-button': AppButton,
    'app-panel': AppPanel,
    'bar-chart': {
      extends: Bar,
      props: ['chartData', 'options', 'canvasId'],
      mounted () {
        if (this.canvasId && this.$el && this.$el.querySelector) {
          const canvas = this.$el.querySelector('canvas')
          if (canvas) {
            canvas.id = this.canvasId
          }
        }
        this.renderChart(this.chartData, this.options)
      }
    },
    'line-chart': {
      extends: Line,
      props: ['chartData', 'options', 'canvasId'],
      mounted () {
        if (this.canvasId && this.$el && this.$el.querySelector) {
          const canvas = this.$el.querySelector('canvas')
          if (canvas) {
            canvas.id = this.canvasId
          }
        }
        Chart.plugins.register({
          afterDatasetsDraw: function (chart) {
            if (chart.tooltip._active && chart.tooltip._active.length) {
              const ctx = chart.ctx
              const activePoint = chart.tooltip._active[0]
              const x = activePoint.tooltipPosition().x
              const topY = chart.chartArea.top
              const bottomY = chart.chartArea.bottom

              ctx.save()
              ctx.beginPath()
              ctx.moveTo(x, topY)
              ctx.lineTo(x, bottomY)
              ctx.lineWidth = 1
              ctx.strokeStyle = "rgba(0,0,0,0.1)"
              ctx.stroke()
              ctx.restore()
            }
          },
        });
        this.renderChart(this.chartData, this.options)
      }
    }
  },
  props: {
    id: {
      type: String,
      default: '-1'
    }
  },
  data () {
    return {
      activateSpinner: false,
      timezone: '+00:00',
      chartOptions2: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ""
              const value = tooltipItem.value
              return `${datasetLabel}: ${value}`
            },
          },
        },
        hover: {
          mode: "index",
          intersect: false,
        },
      },
      chartOptions3: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ""
              const value = tooltipItem.value
              return `${datasetLabel}: ${value}`
            },
          },
        },
        hover: {
          mode: "index",
          intersect: false,
        },
      },
      chartType: 'line',
      dateEnd: '',
      dateStart: '',
      displayFilter: false,
      engineIndex: undefined,
      engineOptions: [],
      IdPod: '',
      flagBPCE: conf.visual != 'BPCE' ? false : true,
      listItems: [],
      listTitle: '',
      metricsChartsData: {},
      metricsChartsDataUnit: {},
      metricsKeys: [],
      metricsUnits: [],
      orgnlDateEnd: '',
      orgnlDateStart: '',
      podTypeOptions: [],
      refreshKey: false,
      stackedBarChartData: null,
      tabName: undefined,
      tabs: [],
      colorMap: [],
      filterValues: {
        dateStart: 1,
        IdPod: ''
      },
      idPodColors:  [
        {
          idPodBase: "",
          backgroundColor: "rgba(230, 159, 0, 0.8)", // Orange
          borderColor: "rgba(230, 159, 0, 1)"
        },
        {
          idPodBase: "",
          backgroundColor: "rgba(86, 180, 233, 0.8)", // Sky Blue
          borderColor: "rgba(86, 180, 233, 1)"
        },
        {
          idPodBase: "",
          backgroundColor: "rgba(0, 158, 115, 0.8)", // Teal Green
          borderColor: "rgba(0, 158, 115, 1)"
        },
        {
          idPodBase: "",
          backgroundColor: "rgba(240, 228, 66, 0.8)", // Yellow
          borderColor: "rgba(240, 228, 66, 1)"
        },
        {
          idPodBase: "",
          backgroundColor: "rgba(0, 114, 178, 0.8)", // Blue
          borderColor: "rgba(0, 114, 178, 1)"
        },
        {
          idPodBase: "",
          backgroundColor: "rgba(213, 94, 0, 0.8)", // Vermilion
          borderColor: "rgba(213, 94, 0, 1)"
        }
      ]
    }
  },
  computed: {
    filterConfig() {
      const dateBeforeOptions = [
        { value: 1, labelKey: 'common.monitoring.before1H' },
        { value: 12, labelKey: 'common.monitoring.before12H' },
        { value: 24, labelKey: 'common.monitoring.before24H' }
      ]
      
      const podOptions = this.podTypeOptions.map(type => ({
        value: type,
        label: type
      }))
      
      return [
        {
          id: 'filter-date-before',
          type: 'select',
          prop: 'dateStart',
          labelKey: 'common.monitoring.dateBefore',
          icon: 'fa-solid fa-clock',
          value: this.filterValues.dateStart,
          options: dateBeforeOptions,
          colSm: '6',
          colLg: '6'
        },
        {
          id: 'filter-pod-type',
          type: 'select',
          prop: 'IdPod',
          labelKey: 'common.monitoring.pod_type',
          icon: 'fa-solid fa-server',
          value: this.filterValues.IdPod,
          defaultOptionKey: 'common.monitoring.select_pod',
          options: podOptions,
          colSm: '6',
          colLg: '6'
        }
      ]
    }
  },
  created () {
    this.timezone = moment.tz.guess()
    
    this.dateStart = 1
    this.orgnlDateStart = 1
    this.filterValues.dateStart = 1
    // this.dateEnd = moment().format('DD/MM/YYYY HH:mm:ss')
    // this.orgnlDateEnd = this.dateEnd
    this.listTitle = this.$i18n.t('Webheader.metricsData')
    this.activateSpinner = true
    let that = this
    this.$engine.engines.forEach((engine, index) => {
      if (that.engineIndex === undefined && engine.common.network.state === 'online') {
        that.engineIndex = engine.common.network.id
      }
      that.engineOptions.push({id: engine.common.network.id, name:engine.common.network.info.name, disabled: engine.common.network.state === 'offline'})
    })
    this.getMetricsData()

    if (this.id !== '-1') {
      this.tabName = parseInt(this.id)
    }
  },
  mounted() {
    this.renderChartsGeneric('key')
    this.renderChartsGeneric('unit')
  },
  updated() {
    this.renderChartsGeneric('key')
    this.renderChartsGeneric('unit')
  },
  watch: {
    tabName(newVal) {
      this.$nextTick(() => {
        this.onTabChange(newVal)
      })
    },
    engineIndex () {
      this.$nextTick(() => {
        this.onTabChange(this.tabName)
      })
    }
  },
  methods: {
    getMetricsKeyTranslation(metricsKey) {
      // Try to translate with the key path first
      const translationKey = 'common.monitoring.key.' + metricsKey
      const translation = this.$t(translationKey)
      
      // If translation is not found (returns the key itself), return the original value
      if (translation === translationKey) {
        return metricsKey
      }
      
      return translation
    },
    arrayBufferToBase64(buffer) {
      let binary = ''
      let bytes = new Uint8Array(buffer)
      let length = bytes.length
      for (let i = 0; i < length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return window.btoa(binary)
    },
    clearFilterFields() {
      this.filterValues.dateStart = 1
      this.filterValues.IdPod = ''
      this.dateStart = this.orgnlDateStart
      this.dateEnd = this.orgnlDateEnd
      this.IdPod = ''
    },
    onFilterChanged({ prop, value }) {
      this.filterValues[prop] = value
      if (prop === 'dateStart') {
        this.dateStart = value
      } else if (prop === 'IdPod') {
        this.IdPod = value
      }
    },
    toggleFilter() {
      this.displayFilter = !this.displayFilter
    },
    convertUnitIfNeeded(value, unit) {
      const standardizedUnit = unit.toLowerCase()

      let newValue = value
      let newUnit = standardizedUnit

      if (newValue.includes('/')) {
        let newValues = newValue.split('/')
        newValue = newValues[0] * 100 / newValues[1]
      }

      if (standardizedUnit === 'ko' && value > 1024) {
        newValue = value / 1024
        newUnit = 'mo'
      } else if (standardizedUnit === 'mo' && value > 1024) {
        newValue = value / 1024
        newUnit = 'go'
      }

      return { value: newValue, unit: newUnit }
    },
    createTabs () {
      const metricsKeyTabs = this.metricsKeys.map((metricsKey, index) => ({
        translationKey: this.getMetricsKeyTranslation(metricsKey),
        metricsKey: metricsKey,
        index: index
      }))

      const metricsUnitTabs = this.metricsUnits.map((metricsUnit, index) => ({
        translationKey: this.$t('common.monitoring.resource_usage_over_time_unit', { metricsUnit: metricsUnit }),
        metricsUnit: metricsUnit,
        index: index + metricsKeyTabs.length
      }))
      // this.tabs = [...metricsKeyTabs, ...metricsUnitTabs]
      this.tabs = [...metricsKeyTabs]
    },
    engineUpdate () {
      this.getMetricsData()
    },
    extractXLSX() {
      this.$f7.showPreloader(this.$i18n.t('common.loading_page_label'))

      let wb = XLSX.utils.book_new()
      let sheetNames = []

      if (this.listItems.length > 0) {
        let sortedList = this.listItems.sort((a, b) => {
          if (a.MetricsKey < b.MetricsKey) return -1
          if (a.MetricsKey > b.MetricsKey) return 1
          return 0
        })

        let groupedByMetricsKey = {}
        sortedList.forEach(result => {
          if (!groupedByMetricsKey[result.MetricsKey]) {
            groupedByMetricsKey[result.MetricsKey] = []
          }
          groupedByMetricsKey[result.MetricsKey].push(result)
        })

        Object.keys(groupedByMetricsKey).forEach(metricsKey => {
          let sheetData = []
          sheetData.push([
            this.$t('common.monitoring.metricsKey'),
            this.$t('common.monitoring.metricsValue'),
            this.$t('common.monitoring.metricsUnit'),
            this.$t('common.monitoring.metricsOperationDate'),
            this.$t('common.monitoring.pod_type')
          ])

          groupedByMetricsKey[metricsKey].forEach(result => {
            const date = result.MetricsOperationDate
            const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date)
            const isoWithTZ = hasTimezone ? date : date + 'Z'
            let formattedHour = moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS')
            sheetData.push([
              this.getMetricsKeyTranslation(result.MetricsKey),
              result.MetricsValue,
              result.MetricsUnit,
              formattedHour,
              result.IdPod
            ])
          })

          let sheetName = this.getMetricsKeyTranslation(metricsKey).substring(0, 29)
          let originalSheetName = sheetName
          let suffix = 1
          while (sheetNames.includes(sheetName)) {
            sheetName = `${originalSheetName}_${suffix}`
            suffix++
          }
          sheetNames.push(sheetName)

          let ws = XLSX.utils.aoa_to_sheet(sheetData)
          XLSX.utils.book_append_sheet(wb, ws, sheetName)
        })
      }

      this.$f7.hidePreloader()
      let wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      let linkSource = 'data:application/octet-stream;base64,' + this.arrayBufferToBase64(wbout)
      let downloadLink = document.createElement('a')
      let today = moment().unix()
      let fileName = this.$t('common.monitoring.monitoring') + '_' + today + '.xlsx'

      if (this.$f7.device.os === 'android' || this.$f7.device.os === 'ios') {
        downloadFileWithDefinedHeaders(this, fileName, linkSource)
      } else {
        downloadLink.href = linkSource
        downloadLink.download = fileName
        downloadLink.click()
      }

      this.$f7.alert('', this.$i18n.t('common.exprt_succ'))
    },
    generateLineChartDataUnique (metricsData, metricsKey = null) {
      if (metricsKey) {
        metricsData = metricsData.filter(item => item.MetricsKey.toLowerCase() === metricsKey.toLowerCase())
      }

      const labels = [...new Set(metricsData.map(item => {
        const date = item.MetricsOperationDate
        const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date)
        const isoWithTZ = hasTimezone ? date : date + 'Z'
        return moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS')
      }))]
      const metricsMap = {}
      const unitsMap = {}
      // console.log(labels)

      metricsData.forEach(item => {
        const key = `${item.MetricsKey}#${item.MetricsUnit}#${item.IdPod}`
        if (!metricsMap[key]) {
          metricsMap[key] = new Array(labels.length).fill(null)
        }

        unitsMap[key] = item.MetricsUnit
      })

      metricsData.forEach(item => {
        const date = item.MetricsOperationDate
        const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date)
        const isoWithTZ = hasTimezone ? date : date + 'Z'
        const index = labels.indexOf(moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS'))
        if (index !== -1) {
          const key = `${item.MetricsKey}#${item.MetricsUnit}#${item.IdPod}`

          let idPod = item.IdPod

          const convertedValue = this.convertUnitIfNeeded(item.MetricsValue, item.MetricsUnit)

          if (convertedValue.unit !== item.MetricsUnit) {
            unitsMap[key] = convertedValue.unit
          }

          metricsMap[key][index] = convertedValue.value
        }
      })

      const datasets = Object.keys(metricsMap).map((key, index) => {
        const [metricsKey, originalUnit, idPod] = key.split('#')
        const finalUnit = unitsMap[key]
        const match = this.idPodColors.find(item => idPod.includes(item.idPodBase))
        return {
          label: `${idPod} (${finalUnit.toUpperCase()})`,
          data: metricsMap[key],
          borderColor: match.borderColor,
          backgroundColor: match.backgroundColor,
          fill: true
        }
      })

      if (metricsKey) {
        this.$set(this.metricsChartsData, metricsKey, {
          labels: labels,
          datasets: datasets
        })
      } else {
        metricsData.forEach(item => {
          const key = item.MetricsKey
          this.$set(this.metricsChartsData, key, {
            labels: labels,
            datasets: datasets
          })
        })
      }

      this.$nextTick(() => {
        this.renderChartsGeneric('key')
      })
    },
    generateLineChartDataByUnit (metricsData, metricsUnit = null) {
      if (metricsUnit) {
        metricsData = metricsData.filter(item => item.MetricsUnit.toLowerCase() === metricsUnit.toLowerCase())
      }

      const labels = [...new Set(metricsData.map(item => {
        const date = item.MetricsOperationDate
        const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date)
        const isoWithTZ = hasTimezone ? date : date + 'Z'
        return moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS')
      }))]
      const metricsMap = {}
      const unitsMap = {}

      metricsData.forEach(item => {
        const key = `${item.MetricsUnit}_${item.MetricsKey}`
        if (!metricsMap[key]) {
          metricsMap[key] = new Array(labels.length).fill(null)
        }

        unitsMap[key] = item.MetricsUnit
      })

      metricsData.forEach(item => {
        const date = item.MetricsOperationDate
        const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date)
        const isoWithTZ = hasTimezone ? date : date + 'Z'
        
        let index
        if (this.flagBPCE){
          index = labels.indexOf(moment(isoWithTZ).tz(this.timezone).add(1,'h').format('DD/MM/YYYY HH:mm:ss.SSS'))
        }else{
          index = labels.indexOf(moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss.SSS'))
        }
        if (index !== -1) {
          const key = `${item.MetricsUnit}_${item.MetricsKey}`

          const convertedValue = this.convertUnitIfNeeded(item.MetricsValue, item.MetricsUnit)

          if (convertedValue.unit !== item.MetricsUnit) {
            unitsMap[key] = convertedValue.unit
          }

          metricsMap[key][index] = convertedValue.value
        }
      })

      const datasets = Object.keys(metricsMap).map((key, index) => {
        const [originalUnit, metricsKey] = key.split('_')
        const finalUnit = unitsMap[key]

        return {
          label: `${metricsKey} (${finalUnit.toUpperCase()})`,
          data: metricsMap[key],
          borderColor: this.getHashColor(key),
          backgroundColor: this.getHashColor(key, 0.2),
          fill: true
        }
      })

      if (metricsUnit) {
        this.$set(this.metricsChartsDataUnit, metricsUnit, {
          labels: labels,
          datasets: datasets
        })
      } else {
        metricsData.forEach(item => {
          const key = item.MetricsUnit
          this.$set(this.metricsChartsDataUnit, key, {
            labels: labels,
            datasets: datasets
          })
        })
      }

      this.$nextTick(() => {
        this.renderChartsGeneric('unit')
      })
    },
    getHashColor(str, alpha = 1, metricsKey, minDistance = 150) {
      if (!this.colorMap[metricsKey]) {
        // Si c'est une nouvelle métrique, on initialise une liste vide
        this.colorMap[metricsKey] = []
      }

      let hash = 0
      str.split('').forEach(char => {
        hash = char.charCodeAt(0) + ((hash << 5) - hash)
      })

      const baseHue = Math.abs(hash % 360) // Génère une teinte de base
      const s = 60 // Saturation fixe
      const l = 50 // Luminosité fixe

      const maxAttempts = 100 // Limite pour éviter une boucle infinie
      let attempts = 0
      let rgb

      // Fonction pour vérifier si une couleur est trop proche d'une autre
      const isColorTooClose = (rgb, existingColors) => {
        for (const color of existingColors) {
          const distance = Math.sqrt(
              Math.pow(rgb[0] - color[0], 2) +
              Math.pow(rgb[1] - color[1], 2) +
              Math.pow(rgb[2] - color[2], 2)
          );
          if (distance < minDistance) return true
        }
        return false
      };

      // Trouver une couleur unique
      do {
        const hue = (baseHue + Math.random() * 360) % 360 // Explore toute la roue chromatique
        rgb = this.hslToRgb(hue, s, l)
        attempts++
        if (attempts > maxAttempts) {
          console.warn(`Impossible de trouver une couleur unique pour MetricsKey: ${metricsKey}. Retour de la couleur la plus proche.`)
          break
        }
      } while (isColorTooClose(rgb, this.colorMap[metricsKey]))

      // Ajouter la couleur générée à la liste associée à metricsKey
      this.colorMap[metricsKey].push(rgb)

      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    },
    hslToRgb(h, s, l) {
      s /= 100
      l /= 100

      const k = n => (n + h / 30) % 12
      const a = s * Math.min(l, 1 - l)
      const f = n =>
          l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))

      return [f(0), f(8), f(4)].map(val => Math.round(val * 255))
    },
    getMetricsData () {
      let that = this
      /*let filters = {
        DateMin: '2024-11-18T19:00:00',
        DateMax: '2024-11-19T10:00:00'
      }*/
      let filters = {}

      if (this.flagBPCE){
        filters = {
          DateMin: this.dateStart ? this.getDateBefore(this.dateStart) : undefined,
          DateMax: this.dateEnd !== '' ? this.dateEnd : undefined,
          IdPod: this.IdPod ? this.IdPod : undefined
        }
      }else{
        filters = {
          DateMin: this.dateStart ? this.getDateBefore(this.dateStart) : undefined,
          DateMax: this.dateEnd !== '' ? this.dateEnd : undefined,
          IdPod: this.IdPod ? this.IdPod : undefined
        }
      }

      this.$engine.get(this.engineIndex).monitoring.metricsData.fetch(filters).then(resp => {
        let metricsDataList = {}

        metricsDataList = resp.data.MetricsData

        that.updateMetricsData(metricsDataList)
        // that.generateStackedBarChartData(metricsDataList)

        let metricsKeys = [...new Set(metricsDataList.map(item => item.MetricsKey))]
        this.metricsKeys = metricsKeys.filter(metricsKey => {
          let unitsForKey = [...new Set(metricsDataList.filter(item => item.MetricsKey === metricsKey).map(item => item.MetricsUnit))]
          return unitsForKey.length === 1
        }).sort()
        const sortedUniqueIdPods = [...new Set(metricsDataList.map(item => this.extractBaseName(item.IdPod)))].sort()

        if (this.podTypeOptions.length === 0) {
          this.podTypeOptions = sortedUniqueIdPods
          this.idPodColors.forEach((color, index) => {
            if (sortedUniqueIdPods[index]) {
              color.idPodBase = sortedUniqueIdPods[index]
            }
          })
        }

        this.metricsKeys.forEach(key => {
          that.generateLineChartDataUnique(metricsDataList, key)
        })

        /* let metricsUnits = [...new Set(metricsDataList.map(item => item.MetricsUnit))]
        this.metricsUnits = metricsUnits.filter(metricsUnit => {
          let keysForUnit = [...new Set(metricsDataList.filter(item => item.MetricsUnit === metricsUnit).map(item => item.MetricsKey))]
          return keysForUnit.length >= 2
        })

        this.metricsUnits.forEach(unit => {
          that.generateLineChartDataByUnit(metricsDataList, unit)
        }) */

        this.createTabs()

        this.$f7.pullToRefreshDone()
        that.activateSpinner = false
        if (that.tabName === undefined) {
          that.tabName = 0
        }
      })
      .catch(error => {
        if (error.code === 'ERR_NETWORK') {
          that.engineOptions[this.engineIndex].disabled = true
          for (let i = 0; i < that.engineOptions.length; i++) {
            if (i !== that.engineIndex) {
              that.engineIndex = i
              break
            }
          }
        } else {
          errorMessageAlert(error, that)
        }
      })
    },
    extractBaseName(id){
      return id.split('-').slice(0, -2).join('-')
    },
    getRandomColor(alpha = 1) {
      const r = Math.floor(Math.random() * 256)
      const g = Math.floor(Math.random() * 256)
      const b = Math.floor(Math.random() * 256)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    getDateBefore(hoursToSubtract){
      const now = moment().tz(this.timezone)
      const adjustedDate = now.subtract(hoursToSubtract, 'hours')
      return adjustedDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    },
    onRefresh() {
      this.refreshKey = !this.refreshKey
      this.activateSpinner = true
      this.getMetricsData()
    },
    onTabChange (index) {
      let chartKey, chartUnit

      if (index < this.metricsKeys.length) {
        chartKey = `${this.chartType}Chart_${this.metricsKeys[index]}`
      }
      else {
        const unitIndex = index - this.metricsKeys.length
        chartUnit = `${this.chartType}Chart_${this.metricsUnits[unitIndex]}`
      }

      if (chartKey) {
        const chart = this.$refs[chartKey]?.[0]
        if (chart && typeof chart.renderChart === 'function') {
          chart.renderChart(
            this.metricsChartsData[this.metricsKeys[index]],
            this.chartType === 'line' ? this.chartOptions2 : this.chartOptions3
          )
        }
      }

      if (chartUnit) {
        const chartUnitRef = this.$refs[chartUnit]?.[0]
        if (chartUnitRef && typeof chartUnitRef.renderChart === 'function') {
          const unitData = this.metricsChartsDataUnit[this.metricsUnits[index - this.metricsKeys.length]]
          chartUnitRef.renderChart(
            unitData,
            this.chartType === 'line' ? this.chartOptions2 : this.chartOptions3
          )
        }
      }
    },
    renderChartsGeneric(type) {
      const items = type === 'unit' ? this.metricsUnits : this.metricsKeys
      const chartsData = type === 'unit' ? this.metricsChartsDataUnit : this.metricsChartsData

      items.forEach(item => {
        const lineChartRef = this.$refs[`lineChart_${item}`]
        if (lineChartRef && chartsData[item]) {
          const chart = lineChartRef.chartInstance
          if (chart) {
            chart.data = chartsData[item]
            chart.options = this.chartOptions2
            chart.update()
          }
        }
      })
    },
    updateMetricsData (metricsDataList) {
      this.listItems = metricsDataList
    }
  }
}
</script>

<style scoped>
/* Force la couleur noire pour les labels de filtres pour un meilleur contraste RGAA */
.form-label.text-dark {
  color: #212529 !important;
}

/* Gutter des rows pour Bootstrap 4 */
.g-3 {
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

.g-3 > [class*="col-"] {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.gx-2 {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

.gx-2 > [class*="col-"] {
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}
</style>
